import s from './video-settings.module.scss'
import { ParticipantPlaceholder, useMediaDeviceSelect, usePersistentUserChoices, usePreviewTracks } from '@livekit/components-react';
import { DeviceSelect, SelectRow } from '@features/appointment/conference-settings/ui/conference-settings-modal/conference-settings-controls';
import { useConferenceSettingsStore } from '@features/appointment/conference-settings';
import { audioBitrateOptions } from '@features/appointment/conference-settings/ui/conference-settings-modal/conference-settings.constants';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef } from 'react';
import { facingModeFromLocalTrack, Track } from 'livekit-client';

export const VideoSettings = () => {
	const {
		userChoices,
		saveVideoInputEnabled,
		saveVideoInputDeviceId,
		saveAudioInputDeviceId
	} = usePersistentUserChoices();
  const {
    selectedAudioBitrate,
    setSelectedAudioBitrate,
    selectedAudioOutputDeviceId,
    setSelectedAudioOutputDeviceId,
  } = useConferenceSettingsStore();

	const videoSettingsForm = useForm({
    mode: "onChange",
    defaultValues: {
      joinWithoutCamera: !userChoices.videoEnabled,
      showSelfOnMeeting: true,
      hideParticipantsVideo: false,
    },
  });

  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: "videoinput",
    requestPermissions: true,
  });

  const selectedVideoDeviceId = activeDeviceId || userChoices.videoDeviceId || "";
  const joinWithoutCamera = videoSettingsForm.watch("joinWithoutCamera");
  const showSelfOnMeeting = videoSettingsForm.watch("showSelfOnMeeting");
  const previewVideoEnabled = !joinWithoutCamera && showSelfOnMeeting;
  const videoElementRef = useRef(null);

  const previewTracks = usePreviewTracks(
    useMemo(
      () => ({
        audio: false,
        video: previewVideoEnabled
          ? {
              deviceId: selectedVideoDeviceId || undefined,
            }
          : false,
      }),
      [previewVideoEnabled, selectedVideoDeviceId]
    )
  );

  const previewVideoTrack = useMemo(
    () => previewTracks?.find((track) => track.kind === Track.Kind.Video),
    [previewTracks]
  );

  const previewFacingMode = useMemo(() => {
    if (!previewVideoTrack) {
      return "undefined";
    }

    const { facingMode } = facingModeFromLocalTrack(previewVideoTrack);
    return facingMode;
  }, [previewVideoTrack]);

  useEffect(() => {
    if (videoElementRef.current && previewVideoTrack) {
      previewVideoTrack.unmute();
      previewVideoTrack.attach(videoElementRef.current);
    }

    return () => {
      previewVideoTrack?.detach();
    };
  }, [previewVideoTrack]);

  useEffect(() => {
    const subscription = videoSettingsForm.watch((value, { name }) => {
      if (name === "joinWithoutCamera") {
        saveVideoInputEnabled(!value.joinWithoutCamera);
      }
    });

    return () => subscription.unsubscribe();
  }, [saveVideoInputEnabled, videoSettingsForm]);

  return (
    <div className={s.card}>
      <div className={s.contentHeader}>
        <div>
          <div className={s.contentTitle}>Видео</div>
          <div className={s.contentSubtitle}>
            Управление камерой и выбор устройства видеозахвата для конференции.
          </div>
        </div>
      </div>

      <div className={s.cardGroup}>
        <div className={s.cameraCard}>
							<div className={s.cameraPreviewInner}>
								{previewVideoTrack && previewVideoEnabled ? (
									<video
										ref={videoElementRef}
										className={s.cameraPreviewVideo}
										width="1280"
										height="720"
										data-lk-facing-mode={previewFacingMode}
									/>
								) : (
									<div className={s.cameraPreviewPlaceholder}>
										<ParticipantPlaceholder />
									</div>
								)}
							</div>

          <FormProvider {...videoSettingsForm}>
            <div className={s.cameraControls}>
              <div className={`${s.settingRow} ${s.settingRowCard}`}>
                <div className={s.settingMeta}>
                  <div className={s.settingLabel}>Камера</div>
                  <div className={s.settingCaption}>
                    {devices.length ? `${devices.length} устройств` : "Устройства не найдены"}
                  </div>
                </div>
                <select
                  className={`${s.select} ${s.cameraSelect}`}
                  value={selectedVideoDeviceId}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setActiveMediaDevice(nextValue, { exact: nextValue !== "default" });
                    saveVideoInputDeviceId(nextValue);
                  }}
                >
                  <option value="">
                    {devices.length ? "Выберите значение" : "Нет доступных устройств"}
                  </option>
                  {devices.map((device, index) => (
                    // deviceId бывает пустым до выдачи разрешения — иначе дублирующийся key ""
                    <option key={device.deviceId || `camera-device-${index}`} value={device.deviceId}>
                      {device.label || `Камера ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </FormProvider>
        </div>
      </div>
			<FormProvider {...videoSettingsForm}>
				<div className={s.cardGroup}>
					<div className={s.groupTitle}>Устройства</div>
					<DeviceSelect
						label="Динамик"
						kind="audiooutput"
						value={selectedAudioOutputDeviceId}
						onPersist={setSelectedAudioOutputDeviceId}
						sectionClassName={s.settingRowCard}
					/>
					<DeviceSelect
						label="Микрофон"
						kind="audioinput"
						value={userChoices.audioDeviceId}
						onPersist={saveAudioInputDeviceId}
						sectionClassName={s.settingRowCard}
					/>
          <SelectRow
            label="Качество аудио"
            caption={audioBitrateOptions.find((option) => option.value === selectedAudioBitrate)?.description}
            value={selectedAudioBitrate}
            options={audioBitrateOptions}
            onChange={setSelectedAudioBitrate}
            sectionClassName={s.settingRowCard}
          />
				</div>
			</FormProvider>
    </div>
	)
}
